// src/lib/turnstile.ts (T1): Cloudflare Turnstile on the sign-in form (spec 2.10, 10.3). The widget renders only
// when site.turnstileSiteKey is non-empty. The script is loaded once, on demand, in explicit-render mode; the
// CSP allows challenges.cloudflare.com for script-src and frame-src (public/_headers).

export const TURNSTILE_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

type RenderOptions = {
  sitekey: string;
  action?: string;
  theme?: "light" | "dark" | "auto";
  size?: "normal" | "flexible" | "compact";
  /** "interaction-only": nothing shows unless Cloudflare needs the person to tick a box. */
  appearance?: "always" | "execute" | "interaction-only";
  callback?: (token: string) => void;
  "expired-callback"?: () => void;
  "error-callback"?: () => void;
};
export type TurnstileApi = {
  render(el: HTMLElement, opts: RenderOptions): string;
  reset(widgetId?: string): void;
  remove(widgetId?: string): void;
  getResponse(widgetId?: string): string | undefined;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let loading: Promise<TurnstileApi> | null = null;

/** Loads the Turnstile script once. Rejects when it cannot load (offline, blocked). */
export function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  loading ??= new Promise<TurnstileApi>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = TURNSTILE_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error("turnstile_unavailable"));
    };
    script.onerror = () => {
      loading = null;
      script.remove();
      reject(new Error("turnstile_unavailable"));
    };
    document.head.appendChild(script);
  });
  return loading;
}

export type TurnstileWidget = {
  /** The current token, or null before the challenge passes or after it expires. */
  token(): string | null;
  /** A fresh challenge (after a failed submit: tokens are single-use). */
  reset(): void;
  remove(): void;
};

/**
 * Renders the widget into `el`. `onToken` fires with each fresh token and with null when it expires or errors.
 */
export async function renderTurnstile(
  el: HTMLElement,
  siteKey: string,
  opts: { onToken?: (token: string | null) => void; action?: string; appearance?: RenderOptions["appearance"] } = {},
): Promise<TurnstileWidget> {
  const api = await loadTurnstile();
  let current: string | null = null;
  const set = (t: string | null) => {
    current = t;
    opts.onToken?.(t);
  };
  const id = api.render(el, {
    sitekey: siteKey,
    theme: "light",
    size: "flexible",
    ...(opts.action ? { action: opts.action } : {}),
    ...(opts.appearance ? { appearance: opts.appearance } : {}),
    callback: (t) => set(t),
    "expired-callback": () => set(null),
    "error-callback": () => set(null),
  });
  return {
    token: () => current,
    reset: () => {
      set(null);
      api.reset(id);
    },
    remove: () => api.remove(id),
  };
}
